import type { WallCard } from './types';

export interface DeleteClusterPromotionResult {
  cards: WallCard[];
  promotedCards: WallCard[];
}

export function createClusterFromCardsInTree(
  cards: WallCard[],
  sourceCardId: string,
  targetCardId: string
): WallCard[] {
  if (sourceCardId === targetCardId) {
    return cards;
  }

  const { cards: cardsWithoutSource, removedCard } = removeCardFromTreeWithMatch(
    cards,
    sourceCardId
  );

  if (!removedCard) {
    return cards;
  }

  return updateCardInTree(cardsWithoutSource, targetCardId, {
    cards: [
      normalizeClusterCard({
        ...removedCard,
        parentId: targetCardId,
        xPos: undefined,
        yPos: undefined,
        zPos: 1,
      }),
    ],
    persistCluster: true,
  });
}

export function moveCardToClusterInTree(
  cards: WallCard[],
  cardId: string,
  clusterId: string,
  zPos: number
): WallCard[] {
  const { cards: cardsWithoutMovedCard, removedCard } = removeCardFromTreeWithMatch(cards, cardId);

  if (!removedCard || removedCard.id === clusterId || containsCard(removedCard.cards, clusterId)) {
    return cards;
  }

  return attachCardToClusterInTree(cardsWithoutMovedCard, clusterId, {
    ...removedCard,
    parentId: clusterId,
    xPos: undefined,
    yPos: undefined,
    zPos,
  });
}

export function detachCardFromClusterInTree(
  cards: WallCard[],
  cardId: string,
  xPos: number,
  yPos: number
): WallCard[] {
  const {
    cards: cardsWithoutDetachedCard,
    removedCard,
    removedFromChildList,
  } = removeCardFromTreeWithMatch(cards, cardId);

  if (!removedCard || (!removedCard.parentId && !removedFromChildList)) {
    return cards;
  }

  return [
    ...cardsWithoutDetachedCard,
    normalizeClusterCard({
      ...removedCard,
      parentId: null,
      xPos,
      yPos,
      zPos: getNextRootZPosition(cardsWithoutDetachedCard),
    }),
  ];
}

export function deleteClusterAndPromoteChildrenInTree(
  cards: WallCard[],
  clusterId: string
): DeleteClusterPromotionResult {
  return promoteClusterChildrenFromList(cards, clusterId, null);
}

export function sortClusterChildrenInTree(
  cards: WallCard[],
  clusterId: string,
  orderedIds: string[]
): WallCard[] {
  return cards.map((card) => {
    if (card.id === clusterId) {
      return normalizeClusterCard({
        ...card,
        cards: card.cards.map((childCard) => {
          const index = orderedIds.indexOf(childCard.id);
          return index >= 0 ? normalizeClusterCard({ ...childCard, zPos: index + 1 }) : childCard;
        }),
      });
    }

    if (card.cards.length > 0) {
      return { ...card, cards: sortClusterChildrenInTree(card.cards, clusterId, orderedIds) };
    }

    return card;
  });
}

export function removeCardFromTreeWithMatch(
  cards: WallCard[],
  cardId: string,
  isChildList = false
): { cards: WallCard[]; removedCard: WallCard | null; removedFromChildList: boolean } {
  let removedCard: WallCard | null = null;
  let removedFromChildList = false;
  const remainingCards = cards.flatMap((card) => {
    if (card.id === cardId) {
      removedCard = card;
      removedFromChildList = isChildList;
      return [];
    }

    const childResult = removeCardFromTreeWithMatch(card.cards, cardId, true);

    if (childResult.removedCard) {
      removedCard = childResult.removedCard;
      removedFromChildList = childResult.removedFromChildList;
    }

    return [{ ...card, cards: normalizeEmptyClusters(childResult.cards) }];
  });

  return { cards: normalizeEmptyClusters(remainingCards), removedCard, removedFromChildList };
}

function promoteClusterChildrenFromList(
  cards: WallCard[],
  clusterId: string,
  parentId: string | null
): DeleteClusterPromotionResult {
  let promotedCards: WallCard[] = [];
  let foundCluster = false;

  const remainingCards = cards.flatMap((card) => {
    if (card.id === clusterId) {
      foundCluster = true;
      const nextZPos = getNextZPosition(cards.filter((siblingCard) => siblingCard.id !== card.id));
      const rootXPos = card.xPos ?? 0;
      const rootYPos = card.yPos ?? 0;

      promotedCards = [...card.cards]
        .sort((a, b) => (a.zPos ?? 0) - (b.zPos ?? 0))
        .map((childCard, index) =>
          normalizeClusterCard({
            ...childCard,
            parentId,
            xPos: parentId ? undefined : Math.round(rootXPos + (index + 1) * 10),
            yPos: parentId ? undefined : Math.round(rootYPos + (index + 1) * 10),
            zPos: nextZPos + index,
          })
        );

      return promotedCards;
    }

    const childResult = promoteClusterChildrenFromList(card.cards, clusterId, card.id);

    if (childResult.promotedCards.length > 0) {
      foundCluster = true;
      promotedCards = childResult.promotedCards;

      return [{ ...card, cards: normalizeEmptyClusters(childResult.cards) }];
    }

    return [card];
  });

  if (!foundCluster) {
    return { cards, promotedCards: [] };
  }

  return {
    cards: normalizeEmptyClusters(remainingCards.sort((a, b) => (a.zPos ?? 0) - (b.zPos ?? 0))),
    promotedCards,
  };
}

function attachCardToClusterInTree(
  cards: WallCard[],
  clusterId: string,
  movedCard: WallCard
): WallCard[] {
  return cards.map((card) => {
    if (card.id === clusterId) {
      const shiftedChildren = card.cards.map((childCard) =>
        (childCard.zPos ?? 0) >= (movedCard.zPos ?? 0)
          ? normalizeClusterCard({ ...childCard, zPos: (childCard.zPos ?? 0) + 1 })
          : childCard
      );

      return normalizeClusterCard({
        ...card,
        cards: [...shiftedChildren, normalizeClusterCard(movedCard)].sort(
          (a, b) => (a.zPos ?? 0) - (b.zPos ?? 0)
        ),
        persistCluster: true,
      });
    }

    if (card.cards.length > 0) {
      return { ...card, cards: attachCardToClusterInTree(card.cards, clusterId, movedCard) };
    }

    return card;
  });
}

function updateCardInTree(cards: WallCard[], cardId: string, patch: Partial<WallCard>): WallCard[] {
  return cards.map((card) => {
    if (card.id === cardId) {
      return normalizeClusterCard({ ...card, ...patch });
    }

    if (card.cards.length > 0) {
      return { ...card, cards: updateCardInTree(card.cards, cardId, patch) };
    }

    return card;
  });
}

function normalizeEmptyClusters(cards: WallCard[]): WallCard[] {
  return cards.map((card) => {
    const normalizedChildren = normalizeEmptyClusters(card.cards);
    return normalizedChildren.length === 0 && !card.persistCluster
      ? normalizeClusterCard({ ...card, cards: normalizedChildren, persistCluster: false })
      : normalizeClusterCard({ ...card, cards: normalizedChildren });
  });
}

function containsCard(cards: WallCard[], cardId: string): boolean {
  return cards.some((card) => card.id === cardId || containsCard(card.cards, cardId));
}

function getNextRootZPosition(cards: WallCard[]) {
  return cards.reduce((nextZPos, card) => Math.max(nextZPos, (card.zPos ?? 0) + 1), 1);
}

function getNextZPosition(cards: WallCard[]) {
  return cards.reduce((nextZPos, card) => Math.max(nextZPos, (card.zPos ?? 0) + 1), 1);
}

function normalizeClusterCard(card: WallCard): WallCard {
  return {
    ...card,
    assignedTo: card.assignedTo ?? '',
    cards: card.cards ?? [],
    children: card.children ?? [],
    collapsed: card.collapsed ?? false,
    color: card.color ?? '#ffff99',
    content: card.content ?? '',
    isLocked: card.isLocked ?? false,
    persistCluster: card.persistCluster ?? false,
    shape: card.shape ?? 'card',
    timeSpan: card.timeSpan ?? -1,
    timeSpanUnit: card.timeSpanUnit ?? '',
    title: card.title ?? '',
    type: card.type ?? 'text',
  };
}
